import {metadaApi} from '@dms/api/base';

import {
  DeleteEmployeeLinkApiArg,
  DeleteEmployeeLinkApiResponse,
  GetAuthorizationProfilesApiArg,
  GetAuthorizationProfilesApiResponse,
  GetEmployeeActionsApiArg,
  GetEmployeeActionsApiResponse,
  GetEmployeeAuthorizationIntegrationApiArg,
  GetEmployeeAuthorizationIntegrationApiResponse,
  GetEmployeeIntegrationApiArg,
  GetEmployeeIntegrationApiResponse,
  GetEmployeeIntegrationsApiResponse,
  GetEmployeeLinkApiArg,
  GetEmployeeLinkApiResponse,
  GetEmployeeMechanicsApiArg,
  GetEmployeeMechanicsApiResponse,
  GetEmployeeOrgranisationalDataApiArg,
  GetEmployeeOrgranisationalDataApiResponse,
  GetEmployeeOrgranisationalDataSalesApiApiArg,
  GetEmployeeOrgranisationalDataSalesApiApiResponse,
  GetEmployeePersonalDataApiArg,
  GetEmployeePersonalDataApiResponse,
  GetEmployeeSimilarSearchApiArg,
  GetEmployeeSimilarSearchApiResponse,
  PatchEmployeeOrganisationalDataApiArg,
  PatchEmployeeOrganisationalDataApiResponse,
  PatchEmployeeOrganisationalDataSalesApiApiArg,
  PatchEmployeeOrganisationalDataSalesApiApiResponse,
  PatchEmployeePersonalDataApiArg,
  PatchEmployeePersonalDataApiResponse,
  PostEmployeeApiArg,
  PostEmployeeApiResponse,
  PutEmployeeActionApiArg,
  PutEmployeeActionApiResponse,
  PutEmployeeLinkApiArg,
  PutEmployeeLinkApiResponse,
  PutEmployeePlannerLinkApiArg,
  PutEmployeePlannerLinkApiResponse,
} from './types';

export const metadaEmployeeApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    postEmployee: build.mutation<PostEmployeeApiResponse, PostEmployeeApiArg>({
      query: (queryArg) => ({
        url: `/core/v1/employee`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    getEmployeeSimilarSearch: build.query<
      GetEmployeeSimilarSearchApiResponse,
      GetEmployeeSimilarSearchApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/similar`,
        params: {firstName: queryArg.firstName, lastName: queryArg.lastName},
      }),
    }),
    getSalesAuthorizationProfiles: build.query<
      GetAuthorizationProfilesApiResponse,
      GetAuthorizationProfilesApiArg
    >({
      query: (params) => ({
        url: `/core/v1/employee/authorization-profile/sales`,
        params,
      }),
      providesTags: ['authorizationProfiles'],
    }),
    getAfterSalesAuthorizationProfiles: build.query<
      GetAuthorizationProfilesApiResponse,
      GetAuthorizationProfilesApiArg
    >({
      query: (params) => ({
        url: `/core/v1/employee/authorization-profile/after-sales`,
        params,
      }),
      providesTags: ['authorizationProfiles'],
    }),
    getEmployeeOrgranisationalData: build.query<
      GetEmployeeOrgranisationalDataApiResponse,
      GetEmployeeOrgranisationalDataApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/organisational-data`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'employeeOrganisationalData', projectId: queryArg.employeeId},
      ],
    }),
    patchEmployeeOrganisationalData: build.mutation<
      PatchEmployeeOrganisationalDataApiResponse,
      PatchEmployeeOrganisationalDataApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/organisational-data`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeeOrganisationalData', projectId: queryArg.employeeId},
        'authorizationProfiles',
        'employeeAuthorizationIntegration',
      ],
    }),
    getEmployeePersonalData: build.query<
      GetEmployeePersonalDataApiResponse,
      GetEmployeePersonalDataApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v2/employee/${queryArg.employeeId}/personal-data`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'employeePersonalData', projectId: queryArg.employeeId},
      ],
    }),
    patchEmployeePersonalData: build.mutation<
      PatchEmployeePersonalDataApiResponse,
      PatchEmployeePersonalDataApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v2/employee/${queryArg.employeeId}/personal-data`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeePersonalData', projectId: queryArg.employeeId},
      ],
    }),
    putEmployeeAction: build.mutation<PutEmployeeActionApiResponse, PutEmployeeActionApiArg>({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/action`,
        method: 'PUT',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeePersonalData', projectId: queryArg.employeeId},
        {type: 'employeeActions', projectId: queryArg.employeeId},
      ],
    }),
    getEmployeeActions: build.query<GetEmployeeActionsApiResponse, GetEmployeeActionsApiArg>({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/actions`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'employeeActions', projectId: queryArg.employeeId},
      ],
    }),
    getEmployeeMechanics: build.query<GetEmployeeMechanicsApiResponse, GetEmployeeMechanicsApiArg>({
      query: (queryArg) => ({
        url: `/core/v2/employee/mechanics`,
        params: queryArg
          ? {
              authorizationProfileId: queryArg?.authorizationProfileId,
              withToOffer: queryArg?.withToOffer,
              isActive: queryArg?.isActive,
              employeeCompetenceId: queryArg?.employeeCompetenceId,
            }
          : undefined,
      }),
    }),
    getEmployeeLink: build.query<GetEmployeeLinkApiResponse, GetEmployeeLinkApiArg>({
      query: (queryArg) => ({
        url: `/core/v1/employee/link`,
        params: {contextTarget: 'USER', ...queryArg},
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'employeeLink', projectId: queryArg.employeeId},
      ],
    }),
    putEmployeeLink: build.mutation<PutEmployeeLinkApiResponse, PutEmployeeLinkApiArg>({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/link/USER/${queryArg.userId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeeLink', projectId: queryArg.employeeId},
      ],
    }),
    getEmployeeAuthorizationIntegration: build.query<
      GetEmployeeAuthorizationIntegrationApiResponse,
      GetEmployeeAuthorizationIntegrationApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/integration`,
        params: {
          userId: queryArg.userId,
        },
      }),
      providesTags: ['employeeAuthorizationIntegration'],
    }),
    getEmployeeIntegration: build.query<
      GetEmployeeIntegrationApiResponse,
      GetEmployeeIntegrationApiArg
    >({
      query: (queryArg) => ({
        url: '/core/v1/employee/link',
        params: {
          employeeId: queryArg.employeeId,
          contextId: queryArg.contextId,
          contextTarget: queryArg.contextTarget,
        },
      }),
      providesTags: (result, error, queryArg) => [{type: 'employeeLink', id: queryArg.employeeId}],
    }),
    getEmployeeIntegrations: build.query<GetEmployeeIntegrationsApiResponse, void>({
      query: () => ({
        url: `/integration/v1/integration/PLANNER/employee`,
      }),
    }),
    deleteEmployeeLink: build.mutation<DeleteEmployeeLinkApiResponse, DeleteEmployeeLinkApiArg>({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/link/${queryArg.contextTarget}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeeLink', id: queryArg.employeeId},
      ],
    }),
    putEmployeePlannerLink: build.mutation<
      PutEmployeePlannerLinkApiResponse,
      PutEmployeePlannerLinkApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/link/PLANNER/${queryArg.contextId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeeLink', id: queryArg.employeeId},
      ],
    }),
    getEmployeeOrgranisationalDataSalesApi: build.query<
      GetEmployeeOrgranisationalDataSalesApiApiResponse,
      GetEmployeeOrgranisationalDataSalesApiApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/organisational-data/sales`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'employeeOrganisationalDataSales', projectId: queryArg.employeeId},
      ],
    }),
    patchEmployeeOrganisationalDataSalesApi: build.mutation<
      PatchEmployeeOrganisationalDataSalesApiApiResponse,
      PatchEmployeeOrganisationalDataSalesApiApiArg
    >({
      query: (queryArg) => ({
        url: `/core/v1/employee/${queryArg.employeeId}/organisational-data/sales`,
        method: 'PATCH',
        body: queryArg.body,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'employeeOrganisationalDataSales', projectId: queryArg.employeeId},
        'authorizationProfiles',
        'employeeAuthorizationIntegration',
      ],
    }),
  }),
});

export const {
  usePatchEmployeeOrganisationalDataSalesApiMutation,
  useGetEmployeeOrgranisationalDataSalesApiQuery,
  useGetAfterSalesAuthorizationProfilesQuery,
  useGetSalesAuthorizationProfilesQuery,
  useGetEmployeeAuthorizationIntegrationQuery,
  usePutEmployeeLinkMutation,
  useGetEmployeeLinkQuery,
  useLazyGetEmployeeLinkQuery,
  usePostEmployeeMutation,
  useLazyGetEmployeeSimilarSearchQuery,
  useGetEmployeeOrgranisationalDataQuery,
  usePatchEmployeeOrganisationalDataMutation,
  useGetEmployeePersonalDataQuery,
  usePatchEmployeePersonalDataMutation,
  usePutEmployeeActionMutation,
  useGetEmployeeActionsQuery,
  useGetEmployeeMechanicsQuery,
  useGetEmployeeIntegrationQuery,
  useGetEmployeeIntegrationsQuery,
  useDeleteEmployeeLinkMutation,
  usePutEmployeePlannerLinkMutation,
} = metadaEmployeeApi;
